import { FormControl, Grid2 } from '@mui/material';
import { DateRange, DateRangeValidationError, PickerChangeHandlerContext } from '@mui/x-date-pickers-pro';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useContext, useMemo } from 'react';
import { UserContext } from '../../../../App';
import AdvertiserAutocomplete from '../../../../components/AdvertiserAutocomplete';
import AgencyAutocomplete from '../../../../components/AgencyAutocomplete';
import Column from '../../../../components/Column';
import {
    DatePickerShortcutWithPrevious,
    DateRangePicker2,
    getShortcutRange,
    previousRangeFromCurrentRange,
} from '../../../../components/DateRangePicker2';
import { Agency } from '../../../../types/Agency';
import { Dealer as Advertiser } from '../../../../types/Dealer';
import { HatDbCriteria } from '../../context/HatDbContext';

export default function HatDBCriteriaForm(props: {
    criteria: HatDbCriteria;
    onChangeCriteria: (criteria: HatDbCriteria) => void;
}) {
    const { criteria, onChangeCriteria } = props;

    const { userContext } = useContext(UserContext);

    let defaultMinDate = dayjs().subtract(13, 'month').startOf('month');
    let defaultMaxDate: Dayjs = dayjs().endOf('day');

    const currentRange: DateRange<Dayjs> = [dayjs(criteria.currentRangeStart), dayjs(criteria.currentRangeEnd)];

    const previousRange: DateRange<Dayjs> = [
        criteria.previousRangeStart && dayjs(criteria.previousRangeStart),
        criteria.previousRangeEnd && dayjs(criteria.previousRangeEnd),
    ];

    const handleAgencyOnChange = useCallback(
        (agency: Agency | null): void => {
            onChangeCriteria({
                ...criteria,
                agencyId: agency?.id || null,
            });
        },
        [criteria, onChangeCriteria]
    );

    const handleAdvertiserOnChange = useCallback(
        (advertiser: Advertiser | null) => {
            onChangeCriteria({
                ...criteria,
                advertiserId: advertiser?.id || null,
            });
        },
        [criteria, onChangeCriteria]
    );

    // These shortcuts are dynamic based on what is selected for the current range.
    const previousRangeShortcuts = useMemo((): DatePickerShortcutWithPrevious[] => {
        const end = dayjs(criteria.currentRangeStart).subtract(1, 'day').endOf('day');
        return [
            {
                label: 'Previous Range',
                getValue: () => {
                    return previousRangeFromCurrentRange([
                        dayjs(criteria.currentRangeStart),
                        dayjs(criteria.currentRangeEnd),
                    ]) as DateRange<Dayjs>;
                },
            },
            {
                label: 'Previous 30 Days',
                getValue: () => {
                    return [end.subtract(30, 'day').startOf('day'), end];
                },
            },
            {
                label: 'Previous 60 Days',
                getValue: () => {
                    return [end.subtract(60, 'day').startOf('day'), end];
                },
            },
            {
                label: 'Previous 90 Days',
                getValue: () => {
                    return [end.subtract(90, 'day').startOf('day'), end];
                },
            },
        ];
    }, [criteria.currentRangeStart, criteria.currentRangeEnd]);

    const advertiserCritieria = useMemo(() => {
        return { agencyIds: criteria?.agencyId ? [criteria.agencyId] : [] };
    }, [criteria.agencyId]);

    // Use a 3 column layout for regular users and a 4 column layout for admins.
    const lgColumns = userContext.isAdmin() ? 3 : 4;

    function handleCurrentRangeChanged(
        range: DateRange<Dayjs>,
        context: PickerChangeHandlerContext<DateRangeValidationError>
    ) {
        // If the previous range is in use, update it when the current range is modified.
        let newPreviousRange;
        if (criteria.previousRangeStart) {
            if (context.shortcut) {
                // The shortcut may be linked to a previous range. (.e.g: thisWeek -> lastWeek)
                const systemShortcut = context.shortcut as DatePickerShortcutWithPrevious;
                newPreviousRange = getShortcutRange(systemShortcut?.previous);
            }
            if (!newPreviousRange) {
                // Calculate the previous range by shifting the current range into the past.
                newPreviousRange = previousRangeFromCurrentRange(range);
            }
        }

        if (range[0] && range[1]) {
            onChangeCriteria({
                ...criteria,
                currentRangeStart: range[0],
                currentRangeEnd: range[1],
                previousRangeStart: newPreviousRange?.[0] ?? null,
                previousRangeEnd: newPreviousRange?.[1] ?? null,
            });
        }
    }

    return (
        <Grid2 container spacing={2}>
            {userContext.isAdmin() && (
                <Grid2 size={{ sm: 12, md: 6, lg: lgColumns }}>
                    <FormControl fullWidth>
                        <AgencyAutocomplete size="small" value={criteria.agencyId} onChange={handleAgencyOnChange} />
                    </FormControl>
                </Grid2>
            )}

            <Grid2 size={{ sm: 12, md: 6, lg: lgColumns }}>
                <Column gap={1}>
                    <FormControl fullWidth>
                        <AdvertiserAutocomplete
                            size="small"
                            value={criteria.advertiserId}
                            criteria={advertiserCritieria}
                            onChange={handleAdvertiserOnChange}
                            showInactiveAdvertisersToggle={true}
                        />
                    </FormControl>
                </Column>
            </Grid2>

            <Grid2 size={{ sm: 12, md: 6, lg: lgColumns }}>
                <FormControl fullWidth>
                    <DateRangePicker2
                        range={currentRange}
                        label="Date Range"
                        min={defaultMinDate}
                        max={defaultMaxDate}
                        shortcuts={[
                            'today',
                            'thisWeek',
                            'thisMonth',
                            'thisQuarter',
                            'last30Days',
                            'last60Days',
                            'last90Days',
                        ]}
                        onAccept={handleCurrentRangeChanged}
                    />
                </FormControl>
            </Grid2>

            <Grid2 size={{ sm: 12, md: 6, lg: lgColumns }}>
                <FormControl fullWidth>
                    <DateRangePicker2
                        range={previousRange}
                        label="Compare With"
                        min={defaultMinDate}
                        max={defaultMaxDate}
                        shortcuts={['yesterday', 'lastWeek', 'lastMonth', 'lastQuarter', ...previousRangeShortcuts]}
                        clearable={true}
                        onAccept={(range) => {
                            onChangeCriteria({
                                ...criteria,
                                previousRangeStart: range[0] ?? null,
                                previousRangeEnd: range[1] ?? null,
                            });
                        }}
                    />
                </FormControl>
            </Grid2>
        </Grid2>
    );
}
